import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';

const Faq = dynamic(() => import('@/components/growth/faq'));
const HowItWorks = dynamic(() => import('@/components/growth/how-it-works'));
const LocalTrips = dynamic(() => import('@/components/growth/local-trips'));
const NavBar = dynamic(() =>
  import('@/pages/home-page/components/nav-bar/nav-bar'),
);
const Reviews = dynamic(() => import('@/components/growth/reviews'));
const SearchSection = dynamic(() =>
  import('@/components/growth/search-section-city'),
);
const ServiceMap = dynamic(() => import('@/components/growth/service-map'));

import MerchandisingUniversalTopBar from '@/components/merchandising/merchandising-universal-top-bar';
import Page from '@/components/page';
import { useCombinedServiceAreaDetails } from '@/hooks/use-service-area-details';
import useTranslation from '@/hooks/use-translation';
import { getStaticPageWithTemplate } from '@/utils/strapi-cms/page-utils';
import withCMS, { PageComponentProps } from '@/utils/strapi-cms/with-cms';
import { SearchbarToggleProvider } from '../home-page/components/nav-bar/desktop-nav-bar-search/search-bar-context';

const { getStaticPaths, getStaticProps } =
  getStaticPageWithTemplate('/road-trips');
export { getStaticPaths, getStaticProps };

type ShadedContainerProps = {
  padding?: string;
};

const ServiceAreas = ({ slug, metadata, navBarData }: PageComponentProps) => {
  const { t } = useTranslation();
  const serviceAreaId = t('service-area.id');
  const neighborhoods: string[] = t('service-area.neighborhoods').split('\n');
  const serviceAreaList = serviceAreaId.split(',');
  const { data: serviceAreaData, isLoading } = useCombinedServiceAreaDetails(
    serviceAreaList,
    !!serviceAreaId,
  );

  return (
    <>
      <SearchbarToggleProvider
        isStickySearchBar={navBarData.fixedSearchOnScroll}
      >
        <MerchandisingUniversalTopBar />
        <NavBar navState={navBarData.variant} color={navBarData.color} />
      </SearchbarToggleProvider>
      <Page
        title={t('page.title')}
        top={{
          mobile: 0,
          web: 0,
        }}
        metaTags={{
          title: metadata?.metaTitle,
          'og:title': metadata?.metaTitle,
          description: metadata?.metaDescription,
          'og:description': metadata?.metaDescription,
        }}
      >
        <SearchSection
          city={t('page.city')}
          bg={slug}
          lightText={t('page.theme') === 'light'}
          title={t('page.headline')}
          custom="road-trips"
        />
        <ShadedContainer>
          <HowItWorks />
        </ShadedContainer>
        <LocalTrips serviceArea={serviceAreaData} />
        <ServiceMap
          serviceArea={serviceAreaData}
          isLoading={isLoading}
          city={t('service-area.city')}
          neighborhoods={neighborhoods}
        />
        <Reviews />
        <ShadedContainer padding="xSmall">
          <Faq />
        </ShadedContainer>
      </Page>
    </>
  );
};

export default withCMS(ServiceAreas);

const ShadedContainer = styled.div<ShadedContainerProps>`
  ${({ theme, padding }) => css`
    background: rgba(9, 138, 34, 0.05);
    padding: ${padding ? theme.spacings[padding] || 0 : 0}px 0;
  `}
`;
